import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { updateUserStreak } from "../../../../auth/data-access/store/authSlice";
import baseQuery from "../../../../shared/data-access/store/baseQuery";
import { StreakState } from "../../../../shared/interfaces/streak.types";

/**
 * The streak endpoints. Both return the same payload as `character.streak` on `/user/info`,
 * so whatever answers last is still the whole truth.
 */
export const streakApi = createApi({
  reducerPath: "streakApi",
  baseQuery,
  endpoints: (builder) => ({
    /**
     * The dedicated read. `/user/info` already carries the same payload on every app open and
     * after every game, so this exists for the streak page itself: a deep link or a refresh
     * there gets fresh state, and the request also settles any awards still owed.
     */
    getStreak: builder.query<StreakState, void>({
      query: () => "user/streak",
    }),
    /**
     * FR-31: acknowledge the broken / recovered modal. The backend clears the flag and nothing
     * else, so dismissing the modal never costs the subscriber their remaining recovery window.
     */
    markStreakModalSeen: builder.mutation<StreakState, void>({
      query: () => {
        return {
          url: "user/streak/modal-seen",
          method: "POST",
        };
      },
      // Same payload as `character.streak`, so fold it back into auth state for the home card,
      // menu entry and streak page without importing streakApi into authSlice (circular dep).
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        void arg;
        try {
          const { data } = await queryFulfilled;
          dispatch(updateUserStreak(data));
        } catch {
          // Leave streak state unchanged when the modal dismiss request fails.
        }
      },
    }),
  }),
});

export const { useGetStreakQuery, useMarkStreakModalSeenMutation } = streakApi;
